import Vue, { VueConstructor } from 'vue'

type Overlay = Vue & { isActive: boolean }
type Origin = { table: number, token: string }

// Selection belongs to open dialogs, never to the table's checkbox model.
export const rowModalState = Vue.observable({ tokens: [] as string[] })
const origins = new Map<string, Origin>()
const overlays = new Map<Overlay, { origin?: Origin, dialog: boolean }>()
let pendingOrigin: Origin | undefined
let nextTable = 0

export function captureRowModalOrigin () {
  return pendingOrigin?.token || ''
}

export function createRowModalTable () {
  return ++nextTable
}

export function registerRowOrigin (table: number, key: string) {
  const token = `modal-source-${table}-${encodeURIComponent(key)}`
  origins.set(token, { table, token })

  return token
}

function refreshHighlights () {
  const latestByTable = new Map<number, string>()
  overlays.forEach(({ origin, dialog }) => {
    if (dialog && origin && origins.has(origin.token)) {
      latestByTable.set(origin.table, origin.token)
    }
  })
  rowModalState.tokens = Array.from(latestByTable.values())
}

function removeRowOrigin (token: string) {
  origins.delete(token)
  if (pendingOrigin?.token === token) {
    pendingOrigin = undefined
  }
  refreshHighlights()
}

function installNativeRows (VueClass: VueConstructor) {
  const tables = new WeakMap<Element, number>()
  const rows = new WeakMap<HTMLElement, { token: string, stop:() => void }>()
  let nextRow = 0
  const clear = (element: HTMLElement) => {
    const row = rows.get(element)
    if (row) {
      row.stop()
      element.classList.remove(row.token, 'modal-origin-row')
      rows.delete(element)
      removeRowOrigin(row.token)
    }
  }
  const bind = (element: HTMLElement, context?: Vue) => {
    const table = element.closest('table')
    if (!table || !context) {
      return
    }
    if (!tables.has(table)) {
      tables.set(table, createRowModalTable())
    }
    const token = registerRowOrigin(tables.get(table)!, `native:${++nextRow}`)
    const apply = () => {
      element.classList.add(token)
      element.classList.toggle('modal-origin-row', rowModalState.tokens.includes(token))
    }
    rows.set(element, { token, stop: context.$watch(() => rowModalState.tokens, apply) })
    apply()
  }
  VueClass.directive('modal-row', {
    inserted (element, _binding, vnode) {
      bind(element, vnode.context)
    },
    componentUpdated (element, binding, vnode) {
      if (binding.value !== binding.oldValue) {
        clear(element)
        bind(element, vnode.context)
      }
      const row = rows.get(element)
      if (row) {
        element.classList.add(row.token)
        element.classList.toggle('modal-origin-row', rowModalState.tokens.includes(row.token))
      }
    },
    unbind: clear
  })
}

export function removeRowModalTable (table: number, retainedTokens = new Set<string>()) {
  origins.forEach((origin, token) => {
    if (origin.table === table && !retainedTokens.has(token)) {
      origins.delete(token)
    }
  })
  if (pendingOrigin?.table === table && !origins.has(pendingOrigin.token)) {
    pendingOrigin = undefined
  }
  refreshHighlights()
}

function captureOrigin (event: Event) {
  if (event instanceof KeyboardEvent && !['Enter', ' ', 'ArrowDown', 'ArrowUp'].includes(event.key)) {
    return
  }
  const target = event.target
  if (!(target instanceof Element)) {
    pendingOrigin = undefined

    return
  }
  const row = target.closest('tr')
  const token = row && Array.from(row.classList).find(className => origins.has(className))
  if (token) {
    pendingOrigin = origins.get(token)

    return
  }

  // Vuetify detaches menu/dialog content from its originating table.
  // Resolve the innermost active overlay so nested tables keep their own origin.
  const content = target.closest('.v-menu__content, .v-dialog__content')
  pendingOrigin = undefined
  overlays.forEach(({ origin }, overlay) => {
    if (overlay.isActive && overlay.$refs.content === content) {
      pendingOrigin = origin
    }
  })
}

export const RowModalHighlight = {
  install (VueClass: VueConstructor) {
    installNativeRows(VueClass)
    document.addEventListener('click', captureOrigin, true)
    document.addEventListener('keydown', captureOrigin, true)
    VueClass.mixin({
      created (this: Vue) {
        const name = this.$options.name
        if (name !== 'v-dialog' && name !== 'v-menu') {
          return
        }
        const overlay = this as Overlay
        this.$watch(() => overlay.$attrs['row-modal-origin'], (token: string) => {
          if (overlay.isActive && name === 'v-dialog') {
            overlays.set(overlay, { origin: origins.get(token), dialog: true })
            refreshHighlights()
          }
        })
        this.$watch('isActive', (active: boolean) => {
          if (active) {
            const explicitOrigin = overlay.$attrs['row-modal-origin']
            const origin = explicitOrigin === undefined ? pendingOrigin : origins.get(explicitOrigin)
            overlays.set(overlay, { origin, dialog: name === 'v-dialog' })
          } else {
            const previous = overlays.get(overlay)
            if (previous?.dialog && pendingOrigin === previous.origin) {
              // A confirmation can replace this dialog in the same update.
              // Allow its watcher to inherit the origin before clearing it.
              this.$nextTick(() => {
                if (pendingOrigin === previous.origin) {
                  pendingOrigin = undefined
                }
              })
            }
            overlays.delete(overlay)
          }
          refreshHighlights()
        }, { immediate: true })
      },
      beforeDestroy (this: Vue) {
        if (overlays.delete(this as Overlay)) {
          refreshHighlights()
        }
      }
    })
  }
}
